import { createApi } from '@reduxjs/toolkit/query/react';
import { BackendResponse } from '../../types';
import { parseQueryParams } from '../../utils';
import { baseQueryWithReauth } from './baseQuery';

const advancedPaymentApi = createApi({
    reducerPath: 'advancedPayment',
    baseQuery: baseQueryWithReauth,
    tagTypes: ['AdvanceAmountRequestCategory', 'AdvancedPaymentUniversalFieldSet', 'AdvancedPaymentFormFieldMapping', 'AdvancedPaymentApprovalMapping', 'AdvancedPaymentRequest'],
    endpoints: builder => ({
        // ================= ADVANCE AMOUNT REQUEST CATEGORY =================
        getAdvanceAmountRequestCategories: builder.query<BackendResponse<{ count: number; list: any[] }>, any>({
            query: query => ({
                url: `/advanced-payment-master/category${parseQueryParams(query)}`,
                method: 'GET',
            }),
            providesTags: ['AdvanceAmountRequestCategory'],
        }),

        createAdvanceAmountRequestCategory: builder.mutation<BackendResponse<any>, any>({
            query: payload => ({
                url: `/advanced-payment-master/category`,
                method: 'POST',
                body: payload,
            }),
            invalidatesTags: (_, error) => (error ? [] : ['AdvanceAmountRequestCategory']),
        }),

        updateAdvanceAmountRequestCategory: builder.mutation<BackendResponse<void>, { id: number;[key: string]: any }>({
            query: ({ id, ...rest }) => ({
                url: `/advanced-payment-master/category/${id}`,
                method: 'PATCH',
                body: rest,
            }),
            invalidatesTags: (_, error) => (error ? [] : ['AdvanceAmountRequestCategory']),
        }),

        deleteAdvanceAmountRequestCategories: builder.mutation<BackendResponse<void>, number[] | string[] | number | string>({
            query: ids => ({
                url: `/advanced-payment-master/category`,
                method: 'DELETE',
                body: { ids },
            }),
            invalidatesTags: (_, error) => (error ? [] : ['AdvanceAmountRequestCategory']),
        }),

        // ================= UNIVERSAL FIELD SET =================
        getAdvancedPaymentUniversalFields: builder.query<BackendResponse<{ count: number; list: any[] }>, any>({
            query: query => ({
                url: `/advanced-payment-master/universal-set${parseQueryParams(query)}`,
                method: 'GET',
            }),
            providesTags: ['AdvancedPaymentUniversalFieldSet'],
        }),

        createAdvancedPaymentUniversalField: builder.mutation<BackendResponse<any>, any>({
            query: payload => ({
                url: `/advanced-payment-master/universal-set`,
                method: 'POST',
                body: payload,
            }),
            invalidatesTags: (_, error) => (error ? [] : ['AdvancedPaymentUniversalFieldSet']),
        }),

        updateAdvancedPaymentUniversalField: builder.mutation<BackendResponse<void>, { id: number;[key: string]: any }>({
            query: ({ id, ...rest }) => ({
                url: `/advanced-payment-master/universal-set/${id}`,
                method: 'PUT',
                body: rest,
            }),
            invalidatesTags: (_, error) => (error ? [] : ['AdvancedPaymentUniversalFieldSet']),
        }),

        deleteAdvancedPaymentUniversalFields: builder.mutation<BackendResponse<void>, number[] | string[] | number | string>({
            query: ids => ({
                url: `/advanced-payment-master/universal-set`,
                method: 'DELETE',
                body: { ids },
            }),
            invalidatesTags: (_, error) => (error ? [] : ['AdvancedPaymentUniversalFieldSet']),
        }),

        // ================= FORM FIELD MAPPING =================
        getAdvancedPaymentFormFieldMappings: builder.query<BackendResponse<{ count: number; list: any[] }>, any>({
            query: query => ({
                url: `/advanced-payment-master/form-field-mappings${parseQueryParams(query)}`,
                method: 'GET',
            }),
            providesTags: ['AdvancedPaymentFormFieldMapping'],
        }),

        getAdvancedPaymentFormFieldMappingById: builder.query<BackendResponse<any>, number>({
            query: id => ({
                url: `/advanced-payment-master/form-field-mappings/${id}`,
                method: 'GET',
            }),
            providesTags: (_, __, id) => [{ type: 'AdvancedPaymentFormFieldMapping', id }],
        }),

        createAdvancedPaymentFormFieldMapping: builder.mutation<BackendResponse<any>, any>({
            query: payload => ({
                url: `/advanced-payment-master/form-field-mappings`,
                method: 'POST',
                body: payload,
            }),
            invalidatesTags: (_, error) => (error ? [] : ['AdvancedPaymentFormFieldMapping']),
        }),

        updateAdvancedPaymentFormFieldMapping: builder.mutation<BackendResponse<void>, { id: number; body: any }>({
            query: ({ id, body }) => ({
                url: `/advanced-payment-master/form-field-mappings/${id}`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: (_, error) => (error ? [] : ['AdvancedPaymentFormFieldMapping']),
        }),

        deleteAdvancedPaymentFormFieldMappings: builder.mutation<BackendResponse<void>, { ids: number[] | string[] }>({
            query: ({ ids }) => ({
                url: `/advanced-payment-master/form-field-mappings`,
                method: 'DELETE',
                body: { ids },
            }),
            invalidatesTags: (_, error) => (error ? [] : ['AdvancedPaymentFormFieldMapping']),
        }),

        // ================= APPROVAL MAPPINGS =================
        getAdvancedPaymentApprovalMappings: builder.query<BackendResponse<{ count: number; list: any[] }>, any>({
            query: query => ({
                url: `/advanced-payment-approval-mappings${parseQueryParams(query)}`,
                method: 'GET',
            }),
            providesTags: ['AdvancedPaymentApprovalMapping'],
        }),

        getAdvancedPaymentApprovalMappingById: builder.query<BackendResponse<any>, number>({
            query: id => ({
                url: `/advanced-payment-approval-mappings/${id}`,
                method: 'GET',
            }),
            providesTags: (_, __, id) => [{ type: 'AdvancedPaymentApprovalMapping', id }],
        }),

        createAdvancedPaymentApprovalMapping: builder.mutation<BackendResponse<any>, any>({
            query: payload => ({
                url: `/advanced-payment-approval-mappings`,
                method: 'POST',
                body: payload,
            }),
            invalidatesTags: (_, error) => (error ? [] : ['AdvancedPaymentApprovalMapping']),
        }),

        updateAdvancedPaymentApprovalMapping: builder.mutation<BackendResponse<void>, { id: number;[key: string]: any }>({
            query: ({ id, ...rest }) => ({
                url: `/advanced-payment-approval-mappings/${id}`,
                method: 'PUT',
                body: rest,
            }),
            invalidatesTags: (_, error) => (error ? [] : ['AdvancedPaymentApprovalMapping']),
        }),

        deleteAdvancedPaymentApprovalMappings: builder.mutation<BackendResponse<void>, { ids: number[] | string[] }>({
            query: ({ ids }) => ({
                url: `/advanced-payment-approval-mappings`,
                method: 'DELETE',
                body: { ids },
            }),
            invalidatesTags: (_, error) => (error ? [] : ['AdvancedPaymentApprovalMapping']),
        }),

        exportAdvancedPaymentApprovalHierarchy: builder.query<Blob, any>({
            query: query => ({
                url: `/advanced-payment-approval-mappings/export-hierarchy${parseQueryParams(query)}`,
                method: 'GET',
                responseHandler: response => response.blob(),
            }),
        }),

        // ================= ADVANCED PAYMENT REQUESTS =================
        createAdvancedPaymentDraft: builder.mutation<BackendResponse<any>, { companyId?: number }>({
            query: (body) => ({ url: `/advanced-payment`, method: 'POST', body }),
            invalidatesTags: (_, error) => (error ? [] : ['AdvancedPaymentRequest']),
        }),

        getAdvancedPaymentRequests: builder.query<BackendResponse<{ count: number; list: any[] }>, any>({
            query: query => ({ url: `/advanced-payment${parseQueryParams(query)}`, method: 'GET' }),
            providesTags: ['AdvancedPaymentRequest'],
        }),

        getAdvancedPaymentRequestById: builder.query<BackendResponse<any>, number>({
            query: id => ({ url: `/advanced-payment/${id}`, method: 'GET' }),
            providesTags: (_, __, id) => [{ type: 'AdvancedPaymentRequest', id }],
        }),

        updateAdvancedPaymentDraft: builder.mutation<BackendResponse<any>, { id: number;[key: string]: any }>({
            query: ({ id, ...body }) => ({ url: `/advanced-payment/${id}`, method: 'PUT', body }),
            invalidatesTags: (_, error, { id }) => (error ? [] : ['AdvancedPaymentRequest', { type: 'AdvancedPaymentRequest', id }]),
        }),

        submitAdvancedPaymentRequest: builder.mutation<BackendResponse<any>, number>({
            query: id => ({ url: `/advanced-payment/${id}/submit`, method: 'POST' }),
            invalidatesTags: (_, error) => (error ? [] : ['AdvancedPaymentRequest']),
        }),

        deleteAdvancedPaymentRequests: builder.mutation<BackendResponse<void>, number[] | string[]>({
            query: ids => ({ url: `/advanced-payment`, method: 'DELETE', body: { ids } }),
            invalidatesTags: (_, error) => (error ? [] : ['AdvancedPaymentRequest']),
        }),
        getAdvancedPaymentAttachments: builder.query<BackendResponse<any>, number>({
            query: requestId => ({
                url: `/advanced-payment/${requestId}/attachments`,
                method: 'GET',
            }),
            providesTags: (_, __, id) => [
                'AdvancedPaymentRequest',
                { type: 'AdvancedPaymentRequest', id },
            ],
        }),

        uploadAdvancedPaymentAttachment: builder.mutation<BackendResponse<any>, { id: number; file: File; remarks?: string }>({
            query: ({ id, file, remarks }) => {
                const formData = new FormData();
                formData.append('file', file);
                if (remarks) formData.append('remarks', remarks);
                return { url: `/advanced-payment/${id}/attachments`, method: 'POST', body: formData };
            },
            invalidatesTags: (_, error, { id }) => (error ? [] : [{ type: 'AdvancedPaymentRequest', id }]),
        }),

        deleteAdvancedPaymentAttachment: builder.mutation<BackendResponse<void>, { id: number; attachmentId: number }>({
            query: ({ id, attachmentId }) => ({ url: `/advanced-payment/${id}/attachments/${attachmentId}`, method: 'DELETE' }),
            invalidatesTags: (_, error, { id }) => (error ? [] : [{ type: 'AdvancedPaymentRequest', id }]),
        }),

        getAdvancedPaymentAttachmentFile: builder.query<
            Blob,
            { advancedPaymentId: number; attachmentId: number }
        >({
            query: ({ advancedPaymentId, attachmentId }) => ({
                url: `/advanced-payment-attachments/${advancedPaymentId}/file/${attachmentId}`,
                method: 'GET',
                responseHandler: response => response.blob(),
            }),
        }),

        // ─── Approval Action & Logs ────────────────────────────────────────────────
        approveAdvancedPaymentFromEmail: builder.mutation<BackendResponse<any>, any>({
            query: payload => {
                return {
                    url: `/advanced-payment-approvals/approve-from-mail`,
                    method: 'POST',
                    body: payload,
                };
            },
            invalidatesTags: (_, error, { id }) =>
                error
                    ? []
                    : [
                        'AdvancedPaymentRequest',
                    ],
        }),
        advancedPaymentApprovalAction: builder.mutation<BackendResponse<any>, { id: number; action: string; comments?: string }>({
            query: ({ id, ...body }) => ({ url: `/advanced-payment-approvals/${id}`, method: 'POST', body }),
            invalidatesTags: (_, error, { id }) => (error ? [] : ['AdvancedPaymentRequest', { type: 'AdvancedPaymentRequest', id }]),
        }),

        getAdvancedPaymentPendingApprovals: builder.query<BackendResponse<{ list: any[]; count: number }>, any>({
            query: query => ({ url: `/advanced-payment-approvals${parseQueryParams(query)}`, method: 'GET' }),
            providesTags: ['AdvancedPaymentRequest'],
        }),

        bulkAdvancedPaymentApprovalAction: builder.mutation<BackendResponse<any>, { ids: number[]; action: string; comments?: string }>({
            query: body => ({ url: `/advanced-payment-approvals/bulk-action`, method: 'POST', body }),
            invalidatesTags: (_, error) => (error ? [] : ['AdvancedPaymentRequest']),
        }),

        getAdvancedPaymentApprovalLogs: builder.query<BackendResponse<any[]>, number>({
            query: id => ({ url: `/advanced-payment-approvals/logs/${id}/approval`, method: 'GET' }),
            providesTags: (_, __, id) => [{ type: 'AdvancedPaymentRequest', id }],
        }),

        getAdvancedPaymentActivityLogs: builder.query<BackendResponse<any[]>, number>({
            query: id => ({ url: `/advanced-payment-approvals/logs/${id}/activity`, method: 'GET' }),
            providesTags: (_, __, id) => [{ type: 'AdvancedPaymentRequest', id }],
        }),

        getAdvancedPaymentChangeLogs: builder.query<BackendResponse<any[]>, number>({
            query: id => ({ url: `/advanced-payment-approvals/logs/${id}/change`, method: 'GET' }),
            providesTags: (_, __, id) => [{ type: 'AdvancedPaymentRequest', id }],
        }),
        getAdvancedPaymentSapLogs: builder.query<BackendResponse<any[]>, number>({
            query: id => ({ url: `/advanced-payment-approvals/logs/${id}/sap`, method: 'GET' }),
            providesTags: (_, __, id) => [{ type: 'AdvancedPaymentRequest', id }],
        }),

        // ─── Vendor dropdown ───────────────────────────────────────────────────────
        getPoNoForAdvancedPayment: builder.query<BackendResponse<any>, number>({
            query: id => ({ url: `/advanced-payment-master/poNo/${id}`, method: 'GET' }),
        }),
         getTaxNoForAdvancedPayment: builder.query<BackendResponse<{ count: number; list: any[] }>, any>({
            query: query=> ({ url: `/advanced-payment-master/taxNo${parseQueryParams(query)}`, method: 'GET' }),
        }),

        getAdvancedPaymentRecentDrafts: builder.query<BackendResponse<any[]>, void>({
            query: () => ({ url: `/advanced-payment/recent-drafts`, method: 'GET' }),
            providesTags: ['AdvancedPaymentRequest'],
        }),

        getAdvancedPaymentFormFieldMappingByCompany: builder.query<BackendResponse<any>, number >({
            query: id => ({
                url: `/advanced-payment-master/form-field-mappings/by-company/${id}`,
                method: 'GET',
            }),
        }),


        // Print Advanced Payment
        printAdvancedPayment: builder.query<Blob, number>({
            query: id => ({
                url: `/advanced-payment/print/${id}`,
                method: 'GET',
                responseHandler: response => response.blob(),
            }),
        }),

        // Export Advanced Payment Report
        exportAdvancedPaymentReports: builder.query<Blob, { action: string; params?: any }>({
            query: ({ action, params = {} }) => ({
                url: `/advanced-payment/export?action=${action}${Object.entries(params)
                    .filter(([, v]) => v != null && v !== '')
                    .map(([k, v]) => `&${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
                    .join('')}`,
                method: 'GET',
                responseHandler: response => response.blob(),
            }),
        }),

        // Download Advanced Payment JSON
        downloadAdvancedPaymentJSON: builder.mutation<
            BackendResponse<any>,
            { id: number }
        >({
            query: ({ id }) => ({
                url: `/advanced-payment/download/json`,
                method: 'POST',
                body: { id },
            }),
        }),

        // Download Advanced Payment Attachment JSON
        downloadAdvancedPaymentAttachmentJSON: builder.query<
            BackendResponse<any>,
            number
        >({
            query: id => ({
                url: `/advanced-payment/download-attachment-json/${id}`,
                method: 'GET',
            }),
        }),

        // Download All Attachments as ZIP
        downloadAdvancedPaymentAttachmentsZip: builder.query<Blob, number>({
            query: id => ({
                url: `/advanced-payment/${id}/attachments/download-zip`,
                method: 'GET',
                responseHandler: response => response.blob(),
            }),
        }),
        // Push to ERP
        pushAdvancedPaymentToErp: builder.mutation<BackendResponse<any>, { id: number }>(
            {
                query: ({ id }) => ({
                    url: `/advanced-payment/push/erp`,
                    method: 'POST',
                    body: { id },
                }),
                invalidatesTags: ['AdvancedPaymentRequest'],
            },
        ),
    }),
});

export default advancedPaymentApi;

export const {
    usePushAdvancedPaymentToErpMutation,
    useLazyPrintAdvancedPaymentQuery,
    useLazyExportAdvancedPaymentReportsQuery,
    useDownloadAdvancedPaymentJSONMutation,
    useLazyDownloadAdvancedPaymentAttachmentJSONQuery,
    useLazyDownloadAdvancedPaymentAttachmentsZipQuery,
    useGetAdvanceAmountRequestCategoriesQuery,
    useCreateAdvanceAmountRequestCategoryMutation,
    useUpdateAdvanceAmountRequestCategoryMutation,
    useDeleteAdvanceAmountRequestCategoriesMutation,
    useGetAdvancedPaymentUniversalFieldsQuery,
    useCreateAdvancedPaymentUniversalFieldMutation,
    useUpdateAdvancedPaymentUniversalFieldMutation,
    useDeleteAdvancedPaymentUniversalFieldsMutation,
    useGetAdvancedPaymentFormFieldMappingsQuery,
    useGetAdvancedPaymentFormFieldMappingByIdQuery,
    useCreateAdvancedPaymentFormFieldMappingMutation,
    useUpdateAdvancedPaymentFormFieldMappingMutation,
    useDeleteAdvancedPaymentFormFieldMappingsMutation,
    useGetAdvancedPaymentApprovalMappingsQuery,
    useGetAdvancedPaymentApprovalMappingByIdQuery,
    useCreateAdvancedPaymentApprovalMappingMutation,
    useUpdateAdvancedPaymentApprovalMappingMutation,
    useDeleteAdvancedPaymentApprovalMappingsMutation,
    useLazyExportAdvancedPaymentApprovalHierarchyQuery,
    useCreateAdvancedPaymentDraftMutation,
    useGetAdvancedPaymentRequestsQuery,
    useGetAdvancedPaymentRequestByIdQuery,
    useUpdateAdvancedPaymentDraftMutation,
    useSubmitAdvancedPaymentRequestMutation,
    useDeleteAdvancedPaymentRequestsMutation,
    useGetAdvancedPaymentAttachmentFileQuery,
    useLazyGetAdvancedPaymentAttachmentFileQuery,
    useGetAdvancedPaymentAttachmentsQuery,
    useUploadAdvancedPaymentAttachmentMutation,
    useDeleteAdvancedPaymentAttachmentMutation,
    useAdvancedPaymentApprovalActionMutation,
    useGetAdvancedPaymentPendingApprovalsQuery,
    useBulkAdvancedPaymentApprovalActionMutation,
    useApproveAdvancedPaymentFromEmailMutation,
    useGetAdvancedPaymentApprovalLogsQuery,
    useGetAdvancedPaymentActivityLogsQuery,
    useGetAdvancedPaymentChangeLogsQuery,
    useGetAdvancedPaymentSapLogsQuery,
    useGetPoNoForAdvancedPaymentQuery,
    useGetTaxNoForAdvancedPaymentQuery,
    useGetAdvancedPaymentRecentDraftsQuery,
    useGetAdvancedPaymentFormFieldMappingByCompanyQuery,
} = advancedPaymentApi;
